import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EquipmentFormComponent } from '../equipment-form/equipment-form.component';
import { BaseAssetInput, MiniAsset } from '@fullyops/data/assets/asset';
import { AttachmentSet } from '@fullyops/shared/attachment-set';
import { AssetsService } from '@fullyops/domain/assets/service';
import { AssetTypesService } from '@fullyops/domain/asset-types/service';
import { LocationService } from '@fullyops/domain/locations/service';
import { AssetCategoriesService } from '@fullyops/domain/asset-categories/asset-categories.service';
import { ActionTypeLabel } from '@fullyops/data/action-types/entities/action-type-entity';
import { injectCanAssignAssetOwner } from '@fullyops/shared/can-assign-asset-owner';

/**
 * What the panel hands back. `company` is the one the equipment was actually
 * created with, which is null when this user may not set an owner even though a
 * company was asked for — so callers should read it rather than assume.
 */
export type CreatedEquipment = MiniAsset & {
  company: { id: string } | null;
};

/**
 * Creating an equipment from inside another screen. It carries the form, the
 * ownership rules and the create call, so that the sidebar presenting it is only
 * a frame around this, one that triggers `create()` from its own footer.
 */
@Component({
  selector: 'fo-create-equipment-panel',
  templateUrl: './create-equipment-panel.component.html',
  standalone: true,
  imports: [CommonModule, EquipmentFormComponent],
})
export class CreateEquipmentPanelComponent implements OnInit, OnDestroy {
  private assetsService = inject(AssetsService);
  private assetTypesService = inject(AssetTypesService);
  private locationService = inject(LocationService);
  private assetCategoryService = inject(AssetCategoriesService);

  /** The company to assign the new equipment to, or null to leave it unassigned. */
  @Input() companyId: string | null = null;
  /**
   * When set, the asset is always created for `companyId`, as on that company's page, and only its locations are
   * offered. Without it, a user who can't assign owners creates the asset unassigned.
   */
  @Input() lockCompany = false;
  /** Only asset types compatible with this action type are offered; null offers every type. */
  @Input() actionType: ActionTypeLabel | null = null;

  @Output() created = new EventEmitter<CreatedEquipment>();

  @ViewChild(EquipmentFormComponent, { static: true })
  private form!: EquipmentFormComponent;

  /** False while the type is still being chosen, as there is nothing to create yet. */
  get choosingType(): boolean {
    return this.form.choosingType;
  }

  get canCreate(): boolean {
    return !this.form.choosingType && this.form.valid;
  }

  protected value?: BaseAssetInput;
  protected initialValue?: BaseAssetInput;
  protected attachments = new AttachmentSet();

  // Naming a company on create is an ownership write. When this user cannot
  // make one, the machine is created unassigned instead of being refused: on an
  // installation work order the automation claims it as soon as the action is
  // saved, which is the only way ownership moves in that configuration anyway.
  protected canAssignCompany = injectCanAssignAssetOwner();

  private chosenTypeId: string | null = null;
  private parentsRepository = this.assetsService.createRepository();
  protected parents$ = this.parentsRepository.assets$;
  protected assetTypes$ = this.assetTypesService.types$;
  protected locations$ = this.locationService.locations$;
  protected categories$ = this.assetCategoryService.categories$;

  ngOnInit() {
    this.initialValue = {
      name: '',
      active: true,
      companyId: this.companyId,
      locationId: null,
      description: '',
      criticality: null,
      warrantyExpiresAt: null,
      installedAt: null,
      externalId: '',
      categoryId: null,
      parentId: null,
      typeId: null,
      classSpecificProperties: null,
      typeSpecificProperties: {},
    };
    if (this.actionType == null) {
      this.assetTypesService.refresh();
    } else {
      this.assetTypesService.refreshByActionType(this.actionType);
    }
    this.assetCategoryService.refresh();
    if (this.companyId != null) {
      this.locationService.refreshByCompanyId(this.companyId);
    }
  }

  ngOnDestroy() {
    this.attachments.clear();
    this.assetTypesService.clear();
    this.assetCategoryService.clear();
    this.parentsRepository.destroy();
    if (this.companyId != null) {
      this.locationService.clearByCompanyId(this.companyId);
    }
  }

  protected onValueChange(v: BaseAssetInput) {
    this.value = v;
    if (v.typeId !== this.chosenTypeId) {
      this.parentsRepository.refresh({ canBeParentOfType: v.typeId });
      this.chosenTypeId = v.typeId;
    }
  }

  create() {
    const value = this.value;
    if (value == null) {
      throw new Error('BUG: tried to create equipment with no value set');
    }

    const owner =
      this.canAssignCompany() || this.lockCompany
        ? { companyId: value.companyId, locationId: value.locationId }
        : { companyId: null, locationId: null };
    this.assetsService
      .create(
        { ...value, ...owner, attachmentIds: [] },
        this.attachments.pendingAttachments,
      )
      .subscribe({
        next: (id) => {
          const serialNumber =
            value.classSpecificProperties != null &&
            'serialNumber' in value.classSpecificProperties
              ? value.classSpecificProperties.serialNumber
              : '';
          this.created.emit({
            id,
            name: value.name,
            serialNumber,
            company: owner.companyId == null ? null : { id: owner.companyId },
          });
        },
        // A refused ownership write is reported by the error interceptor; this
        // only keeps the failure from going unhandled.
        error: () => {},
      });
  }
}
